/*
 * Settings → Operations → Well Testing (fieldiq-api#1241, WT-1).
 *
 * Two sections:
 *   - the company defaults (GET / PUT /well-testing/settings): ticket mode,
 *     reading interval, the H2S action limit and the three anomaly thresholds;
 *   - per customer (GET / PUT /well-testing/customers/:id/settings): its own
 *     ticket mode and reading interval (blank = the company default), and who
 *     signs.
 *
 * EMPTY IS NOT ZERO. A blank H2S action limit is "not stated" and no H2S flag
 * fires; a blank threshold turns that flag OFF. Both are sent as null, never
 * as 0 — the API refuses 0 for the limit and the percentages, and a 0 grace
 * would flag every late reading at once. The reading interval is required.
 *
 * A tenant with no row reads the API's defaults with `configured: false`, and
 * the page says so. Ticket modes come from GET /meta with the API's labels.
 * Only changed fields are sent; PUT keeps what it is not handed.
 *
 * Writes are WT_ADMIN (admin, office_staff); everyone else reads with the
 * controls disabled. A 403 is said as a refusal, never as a saved toast.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useUpdateWtCustomerSettings,
  useUpdateWtSettings,
  useWtCustomers,
  useWtCustomerSettingsList,
  useWtMeta,
  useWtSettings,
} from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blankToNull, canAdminWt, catalogLabel, settingsNumber, toInput, wtErrorMessage } from '../../lib/well-testing';
import type { WtCustomerSettings, WtCustomerSettingsUpdate, WtSettings, WtSettingsUpdate } from '../../types/well-testing';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const btnPrimary = 'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed';
const btnSecondary =
  'inline-flex items-center gap-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-semibold text-fieldiq-text hover:bg-fieldiq-dark/60 disabled:opacity-60';

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

/** The nullable number fields: the write key, the read key, the label, and whether minutes. */
const NULLABLE_FIELDS = [
  { key: 'h2s_action_limit_ppm', label: 'H2S action limit (ppm)', integer: false,
    hint: 'Blank: not set, and no H2S flag fires.' },
  { key: 'pressure_drop_pct', label: 'Pressure drop (%)', integer: false,
    hint: 'Flags a reading this much below the last. Blank turns the flag off.' },
  { key: 'rate_change_pct', label: 'Rate change (%)', integer: false,
    hint: 'Flags a rate this much off the last. Blank turns the flag off.' },
  { key: 'interval_grace_min', label: 'Reading grace (min)', integer: true,
    hint: 'Minutes past the interval before a missed reading is flagged. Blank turns the flag off.' },
] as const;

type NullableKey = (typeof NULLABLE_FIELDS)[number]['key'];

const textsOf = (s: WtSettings): Record<NullableKey, string> => ({
  h2s_action_limit_ppm: toInput(s.h2sActionLimitPpm),
  pressure_drop_pct: toInput(s.pressureDropPct),
  rate_change_pct: toInput(s.rateChangePct),
  interval_grace_min: toInput(s.intervalGraceMin),
});

/**
 * The PUT body for what changed, or an error sentence. A cleared nullable field
 * is sent as null (off / not set), never 0.
 */
export function wtSettingsBody(
  settings: WtSettings,
  ticketMode: string,
  intervalText: string,
  texts: Record<NullableKey, string>,
  shift: { start: string; length: string } = { start: settings.shiftStartTime ?? '', length: toInput(settings.shiftLengthHours) },
): { body: WtSettingsUpdate } | { error: string } {
  const body: WtSettingsUpdate = {};
  if (ticketMode !== settings.defaultTicketMode) body.default_ticket_mode = ticketMode;
  if (intervalText.trim() !== toInput(settings.defaultReadingIntervalMin)) {
    const parsed = settingsNumber(intervalText, 'Reading interval', { integer: true });
    if ('error' in parsed) return parsed;
    if (parsed.value === null) return { error: 'Reading interval is required, in whole minutes.' };
    body.default_reading_interval_min = parsed.value;
  }
  const initial = textsOf(settings);
  for (const f of NULLABLE_FIELDS) {
    if (texts[f.key].trim() === initial[f.key]) continue;
    const parsed = settingsNumber(texts[f.key], f.label, { integer: f.integer });
    if ('error' in parsed) return parsed;
    body[f.key] = parsed.value;
  }
  // The shift window (WT-6). Both are required, never cleared to null; the API
  // refuses anything else with a sentence of its own.
  if (shift.start.trim() !== (settings.shiftStartTime ?? '')) {
    const v = shift.start.trim();
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(v)) return { error: 'Day shift starts must be a 24-hour time, HH:MM — for example 06:00.' };
    body.shift_start_time = v;
  }
  if (shift.length.trim() !== toInput(settings.shiftLengthHours)) {
    const parsed = settingsNumber(shift.length, 'Day shift length', { integer: true });
    if ('error' in parsed) return parsed;
    if (parsed.value === null || parsed.value < 1 || parsed.value > 23) {
      return { error: 'Day shift length is required, in whole hours from 1 to 23. The night shift is the rest of the 24.' };
    }
    body.shift_length_hours = parsed.value;
  }
  return { body };
}

export default function WellTestingSettingsPage() {
  const { user } = useAuth();
  const canWrite = canAdminWt(user?.role);
  const query = useWtSettings();
  const catalog = useWtMeta().data?.data ?? null;
  const save = useUpdateWtSettings();
  const settings = query.data?.data ?? null;
  const units = query.data?.meta.units ?? null;
  const failure = queryLoadFailure(query);

  const [ticketMode, setTicketMode] = useState('per_day');
  const [intervalText, setIntervalText] = useState('');
  const [texts, setTexts] = useState<Record<NullableKey, string>>({
    h2s_action_limit_ppm: '',
    pressure_drop_pct: '',
    rate_change_pct: '',
    interval_grace_min: '',
  });
  const [shiftStart, setShiftStart] = useState('');
  const [shiftLength, setShiftLength] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setTicketMode(settings.defaultTicketMode);
      setIntervalText(toInput(settings.defaultReadingIntervalMin));
      setTexts(textsOf(settings));
      setShiftStart(settings.shiftStartTime ?? '');
      setShiftLength(toInput(settings.shiftLengthHours));
    }
  }, [settings]);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading well testing settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="well testing settings" />
      </div>
    );
  }

  const initialTexts = textsOf(settings);
  const dirty =
    ticketMode !== settings.defaultTicketMode ||
    intervalText.trim() !== toInput(settings.defaultReadingIntervalMin) ||
    NULLABLE_FIELDS.some((f) => texts[f.key].trim() !== initialTexts[f.key]) ||
    shiftStart.trim() !== (settings.shiftStartTime ?? '') ||
    shiftLength.trim() !== toInput(settings.shiftLengthHours);

  const onSave = () => {
    setError(null);
    const out = wtSettingsBody(settings, ticketMode, intervalText, texts, { start: shiftStart, length: shiftLength });
    if ('error' in out) return setError(out.error);
    save.mutate(out.body, {
      onSuccess: () => toast.success('Well testing settings saved'),
      onError: (err) => setError(wtErrorMessage(err, 'The settings could not be saved.')),
    });
  };

  return (
    <div className="min-h-full" data-testid="wt-settings-page">
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted" data-testid="wt-settings-readonly">
            Only an admin or office staff can change well testing settings.
          </p>
        )}
        {!settings.configured && (
          <p className="text-xs text-fieldiq-muted" data-testid="wt-settings-defaults">
            These are the defaults. Nobody has saved well testing settings for this company yet.
          </p>
        )}
        {units && (
          <p className="text-xs text-fieldiq-muted" data-testid="wt-settings-units">
            Readings are entered in {units.pressure}, {units.liquidVolume}, {units.gasVolume}, °{units.temperature} and {units.length}{' '}
            by default (your company’s unit system). A hand may type another accepted unit; what they typed is kept.
          </p>
        )}
        <ErrorLine message={error} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label htmlFor="wt-ticket-mode" className="text-sm font-medium text-white block mb-1">Default ticket mode</label>
            <select id="wt-ticket-mode" className={inputCls} value={ticketMode} disabled={!canWrite} onChange={(e) => setTicketMode(e.target.value)}>
              {(catalog?.ticketModes ?? [{ value: ticketMode, label: ticketMode }]).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">Used when a customer has no setting of its own.</p>
          </div>
          <div>
            <label htmlFor="wt-interval" className="text-sm font-medium text-white block mb-1">Reading interval (min)</label>
            <input id="wt-interval" className={inputCls} inputMode="numeric" value={intervalText} disabled={!canWrite} onChange={(e) => setIntervalText(e.target.value)} />
            <p className="text-xs text-fieldiq-muted mt-1">Required. 1 to 1440 minutes between readings.</p>
          </div>
          {NULLABLE_FIELDS.map((f) => (
            <div key={f.key}>
              <label htmlFor={`wt-${f.key}`} className="text-sm font-medium text-white block mb-1">{f.label}</label>
              <input
                id={`wt-${f.key}`}
                className={inputCls}
                inputMode={f.integer ? 'numeric' : 'decimal'}
                value={texts[f.key]}
                placeholder="Not set"
                disabled={!canWrite}
                onChange={(e) => setTexts((t) => ({ ...t, [f.key]: e.target.value }))}
              />
              <p className="text-xs text-fieldiq-muted mt-1">{f.hint}</p>
            </div>
          ))}
        </div>

        {/* The shift window (WT-6, api#1261): what a shift summary counts as a
            day or a night shift, in the company's time zone. */}
        <div data-testid="wt-shift-window">
          <h3 className="text-sm font-semibold text-white mb-2">Shift window</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label htmlFor="wt-shift-start" className="text-sm font-medium text-white block mb-1">Day shift starts</label>
              <input
                id="wt-shift-start"
                type="time"
                className={inputCls}
                value={shiftStart}
                placeholder="06:00"
                disabled={!canWrite}
                onChange={(e) => setShiftStart(e.target.value)}
              />
              <p className="text-xs text-fieldiq-muted mt-1">
                Local time{query.data?.meta.timeZone ? ` (${query.data.meta.timeZone})` : ''}. The night shift starts when the day shift ends.
              </p>
            </div>
            <div>
              <label htmlFor="wt-shift-length" className="text-sm font-medium text-white block mb-1">Day shift length (hours)</label>
              <input
                id="wt-shift-length"
                className={inputCls}
                inputMode="numeric"
                value={shiftLength}
                disabled={!canWrite}
                onChange={(e) => setShiftLength(e.target.value)}
              />
              <p className="text-xs text-fieldiq-muted mt-1">Whole hours, 1 to 23. The night shift is the rest of the 24.</p>
            </div>
          </div>
        </div>

        <CustomerSettingsSection canWrite={canWrite} />
      </div>
    </div>
  );
}

function CustomerSettingsSection({ canWrite }: { canWrite: boolean }) {
  const customers = useWtCustomers();
  const catalog = useWtMeta().data?.data ?? null;
  const list = customers.data ?? [];
  const settings = useWtCustomerSettingsList(list.map((c) => c.id));
  const [editing, setEditing] = useState<string | null>(null);
  const failure = queryLoadFailure(customers);

  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="wt-customer-settings">
      <div>
        <h2 className="text-base font-bold text-white">Per customer</h2>
        <p className="text-xs text-fieldiq-muted">A blank ticket mode or reading interval uses the company default above.</p>
      </div>
      {customers.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading customers" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(customers)} kind={failure} subject="customers" />
      ) : list.length === 0 ? (
        <p className="text-sm text-fieldiq-text">There are no customers yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-fieldiq-border">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Customer</th>
                <th scope="col" className="px-4 py-3">Ticket mode</th>
                <th scope="col" className="px-4 py-3">Reading interval</th>
                <th scope="col" className="px-4 py-3">Signer</th>
                {canWrite && <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {list.map((c, i) => {
                const q = settings[i];
                const cs = q?.data?.data ?? null;
                const rowFailure = q ? queryLoadFailure(q) : null;
                return (
                  <React.Fragment key={c.id}>
                    <tr className="border-t border-fieldiq-border" data-testid="wt-customer-settings-row">
                      <td className="px-4 py-3 font-semibold text-white">{c.name}</td>
                      {q?.isLoading ? (
                        <td colSpan={3} className="px-4 py-3 text-fieldiq-muted">Loading…</td>
                      ) : rowFailure || !cs ? (
                        <td colSpan={3} className="px-4 py-3 text-fieldiq-text">
                          {rowFailure === 'refused' ? 'You do not have access to this customer’s settings.' : 'Could not load this customer’s settings.'}
                        </td>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-fieldiq-text">
                            {cs.ticketMode
                              ? catalogLabel(catalog?.ticketModes, cs.ticketMode)
                              : `Default (${catalogLabel(catalog?.ticketModes, cs.effectiveTicketMode)})`}
                          </td>
                          <td className="px-4 py-3 text-fieldiq-text">
                            {cs.readingIntervalMin != null
                              ? `${cs.readingIntervalMin} min`
                              : cs.effectiveReadingIntervalMin != null
                                ? `Default (${cs.effectiveReadingIntervalMin} min)`
                                : '—'}
                          </td>
                          <td className="px-4 py-3 text-fieldiq-text">
                            {cs.signerName || '—'}
                            {cs.signerTitle && <span className="block text-xs text-fieldiq-muted">{cs.signerTitle}</span>}
                          </td>
                        </>
                      )}
                      {canWrite && (
                        <td className="px-4 py-3 text-right">
                          {cs && editing !== c.id && (
                            <button type="button" className={btnSecondary} onClick={() => setEditing(c.id)} aria-label={`Edit settings for ${c.name}`}>
                              <Pencil className="size-4" aria-hidden="true" />
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    {canWrite && editing === c.id && cs && (
                      <tr className="border-t border-fieldiq-border bg-fieldiq-dark/30">
                        <td colSpan={5} className="px-4 py-4">
                          <CustomerSettingsForm settings={cs} onDone={() => setEditing(null)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CustomerSettingsForm({ settings, onDone }: { settings: WtCustomerSettings; onDone: () => void }) {
  const catalog = useWtMeta().data?.data ?? null;
  const save = useUpdateWtCustomerSettings();
  const [ticketMode, setTicketMode] = useState(settings.ticketMode ?? '');
  const [intervalText, setIntervalText] = useState(toInput(settings.readingIntervalMin));
  const [signerName, setSignerName] = useState(settings.signerName ?? '');
  const [signerTitle, setSignerTitle] = useState(settings.signerTitle ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: WtCustomerSettingsUpdate = {};
    if (ticketMode !== (settings.ticketMode ?? '')) body.ticket_mode = ticketMode || null;
    if (intervalText.trim() !== toInput(settings.readingIntervalMin)) {
      // Blank = the company default (null), never 0.
      const parsed = settingsNumber(intervalText, 'Reading interval', { integer: true });
      if ('error' in parsed) return setError(parsed.error);
      body.reading_interval_min = parsed.value;
    }
    if (signerName !== (settings.signerName ?? '')) body.signer_name = blankToNull(signerName);
    if (signerTitle !== (settings.signerTitle ?? '')) body.signer_title = blankToNull(signerTitle);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    save.mutate(
      { customerId: settings.customerId, body },
      {
        onSuccess: () => {
          toast.success(`Saved settings for ${settings.customerName ?? 'the customer'}`);
          onDone();
        },
        onError: (err) => setError(wtErrorMessage(err, 'The customer settings could not be saved.')),
      },
    );
  };

  return (
    <form onSubmit={onSave} className="space-y-3" data-testid="wt-customer-settings-form">
      <ErrorLine message={error} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block text-sm text-white">
          Ticket mode
          <select className={`${inputCls} mt-1`} value={ticketMode} onChange={(e) => setTicketMode(e.target.value)}>
            <option value="">Company default</option>
            {(catalog?.ticketModes ?? []).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-white">
          Reading interval (min)
          <input
            className={`${inputCls} mt-1`}
            inputMode="numeric"
            placeholder="Company default"
            value={intervalText}
            onChange={(e) => setIntervalText(e.target.value)}
          />
        </label>
        <label className="block text-sm text-white">
          Signer name
          <input className={`${inputCls} mt-1`} value={signerName} onChange={(e) => setSignerName(e.target.value)} />
        </label>
        <label className="block text-sm text-white">
          Signer title
          <input className={`${inputCls} mt-1`} value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondary} onClick={onDone} disabled={save.isPending}>
          Cancel
        </button>
        <button type="submit" className={btnPrimary} disabled={save.isPending}>
          {save.isPending ? 'Saving...' : 'Save customer'}
        </button>
      </div>
    </form>
  );
}
